import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import SFDateRangeSelector from "../components/SFDateRangeSelector";
import PharmacyHaloButton from "../components/Button";
import { phappsRequest } from "../services/apiService";
import {
  PHARMACY_HALO_COLORS,
  DOCLINK_COLORS,
} from "../config/colors";
import { API_PATHS } from "../config/apiConstants";
import DocLinkAnalytics from "../components/DocLinkAnalytics";

const DocLinkAnalyticsPage = () => {
  const { chemistID } = useParams();
  const navigate = useNavigate();
  const [chemistData, setChemistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize date range: last 30 days
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 30);
    return [startDate, today];
  });

  useEffect(() => {
    const fetchChemistData = async () => {
      try {
        setLoading(true);

        const [startDate, endDate] = dateRange;

        const params = {
          DateStart: startDate.toISOString().split("T")[0],
          DateEnd: endDate.toISOString().split("T")[0],
        };

        // DocLink table now provides PHUUID (like Quda), so always use PHUUID
        params.PHUUID = chemistID;

        const response = await phappsRequest(
          API_PATHS.CHEMIST_DATA,
          "GET",
          null,
          params
        );
        if (response && response.ChemistData) {
          setChemistData(response);
        } else {
          setError("Failed to fetch chemist data");
        }
      } catch (err) {
        console.error("Error fetching chemist data:", err);
        setError("Error fetching chemist data");
      } finally {
        setLoading(false);
      }
    };

    if (chemistID) {
      fetchChemistData();
    }
  }, [chemistID, dateRange]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  const backgroundColor = `${DOCLINK_COLORS.LIGHT_PURPLE}40`;

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Typography variant="h6" color="textSecondary">
          Loading chemist data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <PharmacyHaloButton onClick={() => navigate("/doclink")}>
          <ArrowBack sx={{ mr: 1 }} />
          Back to DocLink Dashboard
        </PharmacyHaloButton>
      </Box>
    );
  }

  if (!chemistData || !chemistData.ChemistData) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="textSecondary">
          No chemist data available
        </Typography>
        <PharmacyHaloButton onClick={() => navigate("/doclink")}>
          <ArrowBack sx={{ mr: 1 }} />
          Back to DocLink Dashboard
        </PharmacyHaloButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: backgroundColor,
        p: 3,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        // Extend background to edges by counteracting parent padding
        mx: -2,
        my: -2,
        px: 4,
        py: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1fr auto 1fr" },
          gap: 2,
          alignItems: "center",
        }}
      >
        {/* Left: Back Button */}
        <Box sx={{ justifySelf: { xs: "center", xl: "start" } }}>
          <PharmacyHaloButton onClick={() => navigate("/doclink")}>
            <ArrowBack sx={{ mr: 1 }} />
            Back to Dashboard
          </PharmacyHaloButton>
        </Box>

        {/* Center: Pharmacy Name */}
        <Box sx={{ justifySelf: "center", textAlign: "center" }}>
          <Typography
            variant="h4"
            className="font-bold"
            style={{ color: PHARMACY_HALO_COLORS.BLUE }}
          >
            {chemistData.ChemistData.chemistName}
          </Typography>
        </Box>

        {/* Right: Date Range Selector */}
        <Box
          sx={{ justifySelf: { xs: "center", xl: "end" }, minWidth: "300px" }}
        >
          <SFDateRangeSelector
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
          p: 2,
        }}
      >
        <DocLinkAnalytics chemistData={chemistData.ChemistData} />
      </Box>
    </Box>
  );
};

export default DocLinkAnalyticsPage;
